import React, { useEffect, useState } from 'react';

import { Modal, Form, Badge, Alert } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FolderSelect } from 'components/forms/FolderSelect';
import type { CreateTarget } from 'actions';
import type { Received } from 'validation';
import { toError } from 'helpers/toError';

import { getNodeDefinitions, type NodeDefinitionRow } from 'api/node_definitions/endpoints/getNodeDefinitions';
import { getPythonLibrary, type PythonLibrary } from 'api/python_libraries/endpoints/getPythonLibrary';
import type { SavePythonLibrary } from 'api/python_libraries/endpoints/postPythonLibrary';
import { getPythonLibraryNodeDefinitions } from 'api/python_libraries/endpoints/getPythonLibraryNodeDefinitions';
import { putPythonLibraryNodeDefinitions } from 'api/python_libraries/endpoints/putPythonLibraryNodeDefinitions';

import { useAppDispatch } from 'redux/store';
import { createPythonLibrary, updatePythonLibrary } from '../../../../redux/slices/pythonLibrariesSlice';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import { acceptDegraded } from 'validation';

export interface EditPythonLibraryModalProps {
  show: boolean;
  handleClose: () => void;
  mode: 'create' | 'edit';
  libraryId?: number;
  // Workspace create pre-target: seeds the folder on create (python libraries are zoneless).
  createIn?: CreateTarget;
}

interface LibraryForm {
  python_library_id?: number;
  name: string;
  filename: string;
  description: string;
  content: string;
  folder_id: number | null;
}

const EMPTY_FORM: LibraryForm = { name: '', filename: '', description: '', content: '', folder_id: null };

/**
 * Project the form onto the request body. Every field of `SavePythonLibrary` is a required key, so
 * forgetting one here is a compile error rather than a silently unwritten column — see that type
 * for what the backend does with an absent `description` or `folder_id`.
 *
 * The blank description becomes null (the column is nullable) rather than an empty string, which
 * is what the backend's own `description || null` would have made of it anyway.
 */
const toSave = (form: LibraryForm): SavePythonLibrary => ({
  name: form.name,
  filename: form.filename,
  description: form.description === '' ? null : form.description,
  content: form.content,
  folder_id: form.folder_id,
});

/**
 * Create / edit a Python library. Self-contained: loads the full record by id
 * for edit, fetches the norscom_python node definitions and the ones currently
 * linked (toggled here), persists the library, and (on edit) saves the node-def
 * links. Can be opened from anywhere.
 */
const EditPythonLibraryModal: React.FC<EditPythonLibraryModalProps> = ({ show, handleClose, mode, libraryId, createIn }) => {
  const dispatch = useAppDispatch();

  const [form, setForm] = useState<LibraryForm>(EMPTY_FORM);
  const [allNodeDefs, setAllNodeDefs] = useState<readonly NodeDefinitionRow[]>([]);
  const [linkedNodeDefIds, setLinkedNodeDefIds] = useState<number[]>([]);
  // Whether we hold the COMPLETE current link set. Saving REPLACES the whole set, so anything less
  // than complete must not be written: a set we never read unlinks everything, and a set short by
  // one row deletes that one link. False until proven otherwise.
  //
  // A boolean is enough only because the panel does not render until the whole load has finished
  // (see the dialog-level loading branch) — there is no "still reading" state to distinguish. Show
  // the panel earlier and this needs a third state again.
  const [linksComplete, setLinksComplete] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isEditFormInitialized, setIsEditFormInitialized] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDirty = useUnsavedChanges(show && (mode === 'create' || isEditFormInitialized), mode === 'create' ? form : { form, linkedNodeDefIds });

  // Initialise on open: a blank create form, or load the record + node-def links for edit.
  useEffect(() => {
    if (!show) return;
    if (mode !== 'edit' || libraryId == null) {
      setForm({ ...EMPTY_FORM, folder_id: createIn?.folderId ?? null });
      setLinkedNodeDefIds([]);
      setLinksComplete(false);
      setAllNodeDefs([]);
      setLoadError(null);
      setIsEditFormInitialized(false);
      return;
    }

    setIsEditFormInitialized(false);
    setLinkedNodeDefIds([]);
    setLinksComplete(false);
    setAllNodeDefs([]);
    setLoadError(null);

    void (async () => {
      // The form must NOT be offered when the record did not load: it still holds EMPTY_FORM, so it
      // carries no id, and saving it would fall through to the create branch — a dialog titled
      // "Edit" silently POSTing a second library. Report the failure and let the user close instead.
      //
      // Three outcomes, and they need three different sentences because each asks for a different
      // action (#1035). Note the rejection arm is load-bearing: `getRecord` carries only a 404 into
      // the value channel, so a 5xx or a dropped connection reaches here as a throw — which this
      // IIFE would otherwise drop, leaving a blank "Edit" form and no explanation.
      let answer: Received<PythonLibrary | null>;
      try {
        answer = await getPythonLibrary(libraryId);
      } catch (err) {
        setLoadError(`This Python library could not be loaded: ${toError(err).message}`);
        setIsEditFormInitialized(true);
        return;
      }
      if (answer.result !== 'ok') {
        setLoadError(
          answer.result === 'unreadable'
            ? "This Python library could not be loaded: the server's response could not be read. Please report this."
            : "This Python library could not be loaded: part of the server's response could not be read, so saving it could overwrite that part. Please report this."
        );
        setIsEditFormInitialized(true);
        return;
      }
      const fullLib = answer.value;
      if (fullLib === null) {
        setLoadError('This Python library no longer exists; the list may be out of date. Please refresh.');
        setIsEditFormInitialized(true);
        return;
      }
      setForm({
        python_library_id: fullLib.python_library_id,
        name: fullLib.name,
        filename: fullLib.filename,
        description: fullLib.description ?? '',
        content: fullLib.content,
        folder_id: fullLib.folder_id,
      });
      try {
        const [all, linked] = await Promise.all([
          getNodeDefinitions({ type: 'norscom_python', limit: 1000, offset: 0 }).then(acceptDegraded),
          getPythonLibraryNodeDefinitions(fullLib.python_library_id),
        ]);
        setAllNodeDefs(all.nodes);
        // Only `ok`. This set is written back to a REPLACE-ALL endpoint, so anything short of
        // the whole set deletes the difference for good — and the value alone cannot tell a
        // library with two links from one whose third we could not read.
        //
        // It used to accept a degraded answer as long as no problem was `dropped`. That held
        // while a non-array response failed outright; now `array` substitutes `[]` and reports
        // plain drift, so the one body that means "we got nothing" looked like "there are no
        // links" and the save unlinked everything.
        if (linked.result === 'ok') {
          setLinkedNodeDefIds(linked.value.map((nd) => nd.node_definition_id));
          setLinksComplete(true);
        }
      } catch (err) {
        console.error('Failed to fetch node definitions', err);
      } finally {
        setIsEditFormInitialized(true);
      }
    })();
  }, [show, mode, libraryId, dispatch, createIn]);

  // Keyed rather than driven off the control's `name` attribute: a typo'd name used to add a junk
  // key to the form and silently stop updating that field.
  const setField = <K extends keyof LibraryForm>(key: K, value: LibraryForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleNodeDefToggle = (nodeDefId: number, checked: boolean) => {
    setLinkedNodeDefIds((prev) => (checked ? [...prev, nodeDefId] : prev.filter((id) => id !== nodeDefId)));
  };

  const resetAndClose = () => {
    setForm(EMPTY_FORM);
    setLinkedNodeDefIds([]);
    setLinksComplete(false);
    setAllNodeDefs([]);
    setLoadError(null);
    setIsEditFormInitialized(false);
    handleClose();
  };

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      resetAndClose();
    }
  };

  const canSave = loadError === null && !!form.name && !!form.filename && !!form.content;

  const handleSave = async () => {
    setIsSubmitting(true);
    try {
      if (mode === 'edit' && form.python_library_id) {
        const result = await dispatch(updatePythonLibrary({ id: form.python_library_id, save: toSave(form) }));
        if (updatePythonLibrary.rejected.match(result)) return;
        // Only write the links when the current set was actually read in full. The endpoint
        // REPLACES the whole set, so saving after a failed or partial load would send a short list
        // and unlink node definitions — silently, since the panel would just look that way.
        if (linksComplete) {
          try {
            await putPythonLibraryNodeDefinitions(form.python_library_id, linkedNodeDefIds);
          } catch (err) {
            // DELIBERATELY SWALLOWED, and the user is told the opposite. The record write above has already
            // resolved, so `updatePythonLibrary.fulfilled` has fired its success toast synchronously, and
            // `resetAndClose()` below runs either way — a failed link write closes the dialog on "Python
            // library updated successfully!" with the toggles discarded. That is `main`'s behaviour, kept
            // as-is: reporting it means one outcome per write and a dialog that stays open on a partial
            // save (#1035's `isOutOfSync` rule), which belongs to this dialog's edit-model migration — the
            // M of #757. So the guard above is knowingly one-sided until then: the read half refuses to
            // write a set it could not read, the write half cannot report that it failed to write one.
            console.error('Failed to update node definition links', err);
          }
        }
      } else {
        const result = await dispatch(createPythonLibrary(toSave(form)));
        if (createPythonLibrary.rejected.match(result)) return;
      }
      resetAndClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const libraryForm = (
    <Form>
      <Form.Group className="mb-3">
        <Form.Label>Name</Form.Label>
        <Form.Control type="text" placeholder="Display name (e.g. My Utilities)" value={form.name} onChange={(e) => setField('name', e.target.value)} />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Filename</Form.Label>
        <Form.Control type="text" placeholder="my_utils.py" value={form.filename} onChange={(e) => setField('filename', e.target.value)} />
        <Form.Text className="text-muted">Must end with .py — this is the import name used in scripts.</Form.Text>
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control type="text" placeholder="Optional description" value={form.description} onChange={(e) => setField('description', e.target.value)} />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Folder</Form.Label>
        <FolderSelect value={form.folder_id} onChange={(folder_id) => setField('folder_id', folder_id)} />
      </Form.Group>
      <Form.Group className="mb-3">
        <Form.Label>Content</Form.Label>
        <Form.Control
          as="textarea"
          rows={12}
          value={form.content}
          onChange={(e) => setField('content', e.target.value)}
          style={{ fontFamily: '"Fira Code", "Fira Mono", monospace', fontSize: 13 }}
          placeholder="# Python code here..."
        />
      </Form.Group>
    </Form>
  );

  return (
    <>
      <Modal show={show} onHide={handleRequestClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{mode === 'edit' ? 'Edit Python Library' : 'Create Python Library'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadError !== null ? (
            <Alert variant="danger" className="mb-0">
              {loadError}
            </Alert>
          ) : mode === 'edit' && !isEditFormInitialized ? (
            // Covers the by-id read too, not just the link read. Gating this on the link fetch alone
            // let an EMPTY form render for the whole of the record fetch, and then repopulate.
            <div className="text-muted small">Loading...</div>
          ) : (
            <>
              {libraryForm}

              {mode === 'edit' && (
                <>
                  <hr />
                  <h6>Linked Node Definitions {linkedNodeDefIds.length > 0 && <Badge bg="primary">{linkedNodeDefIds.length}</Badge>}</h6>
                  {!linksComplete ? (
                    // Distinct from "none found": the links could not be read, so saving leaves them
                    // untouched. Saying "no node definitions" here would read as "none are linked".
                    <Alert variant="warning" className="py-2 small mb-0">
                      The linked node definitions could not be loaded, so they are not shown and saving will leave them unchanged.
                    </Alert>
                  ) : allNodeDefs.length === 0 ? (
                    <div className="text-muted small">No norscom_python node definitions found.</div>
                  ) : (
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem', padding: '0.5rem' }}>
                      {allNodeDefs.map((nd) => (
                        <Form.Check
                          key={nd.node_definition_id}
                          type="checkbox"
                          id={`nd-${nd.node_definition_id}`}
                          label={nd.name}
                          checked={linkedNodeDefIds.includes(nd.node_definition_id)}
                          onChange={(e) => handleNodeDefToggle(nd.node_definition_id, e.target.checked)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleRequestClose}>
            Close
          </Button>
          <Button variant="primary" disabled={!canSave || isSubmitting || (mode === 'edit' && !isEditFormInitialized)} onClick={handleSave}>
            {mode === 'edit' ? 'Save and Close' : 'Create Library'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          resetAndClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </>
  );
};

export default EditPythonLibraryModal;
